import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "@/lib/telemetry";
import { Button } from "@/components/ui/button";

const CHUNK_RETRY_KEY = "zeastro:chunk-retry";

export function isChunkLoadError(error: Error): boolean {
  return /chunkloaderror|loading chunk|failed to fetch dynamically imported module|importing a module script failed/i.test(
    `${error.name} ${error.message}`,
  );
}

interface Props {
  children: ReactNode;
  onReload?: () => void;
}

interface State {
  error: Error | null;
}

export class AppRecoveryBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, {
      feature: "app-shell",
      boundary: "global",
      componentStack: info.componentStack,
    });

    if (isChunkLoadError(error) && sessionStorage.getItem(CHUNK_RETRY_KEY) !== window.location.href) {
      sessionStorage.setItem(CHUNK_RETRY_KEY, window.location.href);
      (this.props.onReload ?? (() => window.location.reload()))();
    }
  }

  private retry = () => {
    sessionStorage.removeItem(CHUNK_RETRY_KEY);
    (this.props.onReload ?? (() => window.location.reload()))();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center px-4" role="alert">
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-lg">
          <h1 className="text-xl font-semibold text-foreground">This page did not load</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Please try again. Your saved information has not been changed.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button onClick={this.retry}>Try again</Button>
            <Button variant="outline" onClick={() => window.location.assign("/")}>
              Return home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}